import { useState, type FormEvent, type ReactNode } from 'react';
import { ArrowRightIcon, CaretDownIcon } from '@phosphor-icons/react';
import { Field, PillButton } from '@/components/kit';
import { contactsSchema, type Contacts } from '../draft';

type Errors = Partial<Record<keyof Contacts, string>>;

interface ContactsStepProps {
  initial: Contacts;
  remember: boolean;
  onSubmit: (contacts: Contacts, remember: boolean) => void;
  /** What has been chosen (service and time), shown above the fields. */
  summary?: ReactNode;
}

/** Name, phone and car are required; plate and comment are optional. Errors stay next to the field. */
export function ContactsStep({ initial, remember: initialRemember, onSubmit, summary }: ContactsStepProps) {
  const [values, setValues] = useState<Contacts>(initial);
  const [remember, setRemember] = useState(initialRemember);
  const [errors, setErrors] = useState<Errors>({});
  const [more, setMore] = useState(Boolean(initial.plate || initial.comment));

  const set = (key: keyof Contacts) => (value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    if (errors[key]) setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    const parsed = contactsSchema.safeParse(values);
    if (!parsed.success) {
      const next: Errors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof Contacts;
        if (!next[field]) next[field] = issue.message;
      }
      setErrors(next);
      if (next.plate || next.comment) setMore(true);
      const first = Object.keys(next)[0];
      if (first) window.setTimeout(() => document.getElementById(`contact-${first}`)?.focus(), 0);
      return;
    }
    onSubmit(parsed.data, remember);
  };

  return (
    <form onSubmit={submit} noValidate aria-describedby="contacts-hint">
      <div className="ui-stack" style={{ ['--ui-gap' as string]: 'var(--spacing-4)' }}>
        {summary}
        <Field
          id="contact-name"
          label="Имя"
          value={values.name}
          onChange={(e) => set('name')(e.target.value)}
          autoComplete="name"
          required
          error={errors.name}
        />
        <Field
          id="contact-phone"
          label="Телефон"
          type="tel"
          inputMode="tel"
          placeholder="+7 900 123-45-67"
          value={values.phone}
          onChange={(e) => set('phone')(e.target.value)}
          autoComplete="tel"
          required
          error={errors.phone}
        />
        <Field
          id="contact-car"
          label="Автомобиль"
          placeholder="Марка и модель"
          value={values.car}
          onChange={(e) => set('car')(e.target.value)}
          required
          error={errors.car}
        />

        {more ? (
          <>
            <Field
              id="contact-plate"
              label="Госномер — необязательно"
              value={values.plate ?? ''}
              onChange={(e) => set('plate')(e.target.value)}
              autoCapitalize="characters"
              error={errors.plate}
            />
            <label className="ui-field" htmlFor="contact-comment">
              <span className="ui-field__label">Комментарий — необязательно</span>
              <textarea
                id="contact-comment"
                className="ui-input"
                value={values.comment ?? ''}
                maxLength={500}
                rows={3}
                onChange={(e) => set('comment')(e.target.value)}
                aria-invalid={errors.comment ? true : undefined}
              />
              {errors.comment ? <span className="ui-field__error">{errors.comment}</span> : null}
            </label>
          </>
        ) : (
          <button type="button" className="ui-back" onClick={() => setMore(true)} aria-expanded={false}>
            <CaretDownIcon size={14} aria-hidden />
            Госномер и комментарий
          </button>
        )}

        <label className="ui-check">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          <span>Запомнить контакты на этом устройстве</span>
        </label>
        <p className="ui-caption" id="contacts-hint">
          Регистрация не нужна. Студия позвонит, только если нужно что-то уточнить.
        </p>

        <div className="ui-sheet-actions">
          <PillButton type="submit" variant="primary" size="lg" block endIcon={ArrowRightIcon}>
            Проверить запись
          </PillButton>
        </div>
      </div>
    </form>
  );
}
